/**
 * Get query params for pagination
 */
export const paginationQueryParams = (
  offset: number = 0,
  pageSize: number = 25,
): string => {
  let queryString: string = "";

  if (pageSize) {
    queryString += `&pagesize=${pageSize}`;
  }

  if (offset) {
    queryString += `&offset=${offset}`;
  }

  return "?" + queryString.slice(1);
};

/**
 * Generate unique ID
 */
export const generateUniqueId = (): string => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    (c: string) => {
      const r: number = (Math.random() * 16) | 0;
      const v: number = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    },
  );
};

const isProhibitedExists = (arr: string[]): boolean => {
  const items: string[] = [];
  const first: string = arr[0];
  const last: string | null = arr.length > 1 ? arr[arr.length - 1] : null;

  items.push(first[0]);
  items.push(first[first.length - 1]);
  if (last) {
    items.push(last[0]);
    items.push(last[last.length - 1]);
  }

  return items.some((letter: string) => letter === "-");
};

/**
 * Returns the initials based on given name
 *
 * Name contains other characters than letters: return false
 * 1 word: Capital first letter of first word
 * 2+ words: Capital first letters of first and last word
 */
export const generateInitials = (name?: string): string => {
  const expression: RegExp =
    /^[a-zA-ZäöüÄÖÜßČčĆćžžĐđŠšŽžÉéÀàÁáÈèÙùÂâÊêÎîÔôÛûËëÏïÜüŸÿÇçĄąĆćŁłŃńÓóŚśŹźŻż -\s]*$/;

  if (!name || name.match(expression) === null) {
    return "";
  }

  const words: string[] = name.split(/[\s]+/);
  const count: number = words.length;

  if (isProhibitedExists(words)) {
    return "";
  }

  if (count === 1) {
    return words[0].charAt(0).toUpperCase();
  }

  return (words[0].charAt(0) + words[count - 1].charAt(0)).toUpperCase();
};

/**
 * Escapes a string to be used as pure string in a regular expression.
 * Copied from https://www.npmjs.com/package/escape-string-regexp
 */
export const escapeStringRegexp = (str: string): string => {
  const matchOperatorsRe: RegExp = /[|\\{}()[\]^$+*?.]/g;
  return str.replace(matchOperatorsRe, "\\$&");
};

/**
 * Check if key by keyCode,
 * Valid if not a Function key, special key, backspace, etc
 */
export const isValidKey = (keyCode: number): boolean => {
  return [0, 8].indexOf(keyCode) === -1;
};

/**
 * Check if meta key (cmd, ctrl, alt)
 */
export const isMetaKey = (event: KeyboardEvent): boolean => {
  return event.metaKey || event.ctrlKey || event.altKey;
};

/**
 * Validator for phone number
 */
export const phoneValidatorAssistance: any = {
  isTelephone(str: string): boolean {
    const regExp: RegExp = new RegExp(
      /^[0-9\-()*+#.\s|ext|extension]{1,20}$/,
      "gi",
    );
    return regExp.test(str);
  },

  checkLetters(str: string): boolean {
    const s: string = str.toLowerCase();

    if (s.indexOf("ext") > -1 || s.indexOf("extension") > -1) {
      return false;
    }

    return /[a-z]/.test(s);
  },

  removeSpaces(str: string): string {
    return str.replace(/\s/g, "");
  },

  checkIfDate(str: string): boolean {
    return str.split("/").length === 3;
  },

  isValidFirstLetter(str: string): boolean {
    const char: string = str.charAt(0);
    const allowed: string[] = ["+", "[", "(", "#", "*"];

    return allowed.indexOf(char) > -1 || (char >= "0" && char <= "9");
  },

  /**
   * Checks round brackets (opened and closed)
   */
  checkRoundBrackets(str: string): boolean {
    if (!this.findOccurences(str, "(") && !this.findOccurences(str, ")")) {
      return true;
    }

    const regex: RegExp = new RegExp(/\(([^)]+)\)/);
    const allowedPositions: number[] = [0, 4];

    if (
      !regex.test(str) ||
      this.findOccurences(str, "(") !== 1 ||
      this.findOccurences(str, ")") !== 1
    ) {
      return false;
    }

    const result: string = str[0] === "+" ? str.substr(1) : str;
    const position: number = result.indexOf("(");

    return position >= allowedPositions[0] && position <= allowedPositions[1];
  },

  /**
   * Checks round brackets (opened and closed)
   */
  checkSquareBrackets(str: string): boolean {
    if (!this.findOccurences(str, "[") && !this.findOccurences(str, "]")) {
      return true;
    }

    const regex: RegExp = new RegExp(/\[([^\]]+)]/);
    const allowedPosition: number = 0;

    if (
      !regex.test(str) ||
      this.findOccurences(str, "[") !== 1 ||
      this.findOccurences(str, "]") !== 1
    ) {
      return false;
    }

    const result: string = str[0] === "+" ? str.substr(1) : str;
    const position: number = result.indexOf("[");

    return position === allowedPosition;
  },

  checkIfContainsDots(str: string): boolean {
    return str.indexOf(".") > -1;
  },

  /**
   * Finds occurances
   */
  findOccurences(str: string, char: string): number {
    return str.split(char).length - 1;
  },

  generateNumber(str: string): string {
    // AOT has some problems with regex currently
    // this notation is safe for tdev/prod builds
    const r: RegExp = new RegExp("[\\/()\\[\\]\\-–\\s]", "gi");
    return str.replace(r, "");
  },
};

export const isDTMFIncluded = (phoneNumber: string): boolean => {
  return phoneNumber.split(",").length === 2;
};

export const isValidTelephoneNumber = (phoneNumber: string): boolean => {
  if (phoneValidatorAssistance.checkLetters(phoneNumber)) {
    return false;
  }

  phoneNumber = phoneValidatorAssistance.removeSpaces(phoneNumber);

  if (
    !phoneValidatorAssistance.isValidFirstLetter(phoneNumber) ||
    !phoneValidatorAssistance.checkRoundBrackets(phoneNumber) ||
    !phoneValidatorAssistance.checkSquareBrackets(phoneNumber) ||
    phoneValidatorAssistance.checkIfContainsDots(phoneNumber)
  ) {
    return false;
  }

  phoneNumber = phoneValidatorAssistance.generateNumber(phoneNumber);
  return phoneValidatorAssistance.isTelephone(phoneNumber);
};

// remove space, 0, +, # prefix and special character from phone number i.e. (00 )147 (+)49147 (0)147 (#00)147
export const removePhoneNumberPrefix = (phoneNumber: string): string => {
  return phoneNumber.replace(/[\W_]/g, "").replace(/^[\s0+#]+/g, "");
};

export const removePhoneNumberLeadingPlusOrZero = (
  phoneNumber: string,
): string => {
  return phoneNumber.replace(/^(\+|00)/, "");
};

export const isInternationalPhoneNumber = (phoneNumber: string): boolean => {
  return phoneNumber.startsWith("00") || phoneNumber.startsWith("+");
};

export const isMobileNumber = (phoneNumber: string): boolean => {
  return /^[0+#]/.test(phoneNumber);
};

// decodes %2B to +
export const decodePrefixInPhoneNumber = (phoneNumber: string): string => {
  return phoneNumber.replace(/^%2B/g, "+");
};

export const buildParams = (
  search: string | null = "",
  page: number = 0,
  perPage: number = 25,
  sort?: string,
  restrictions?: string,
): string => {
  const queryParams: string[] = [`page=${page}`];

  // Additional check (typeof search === "string").
  // As it could be a situation when subscriber will pass an object
  if (search && typeof search === "string") {
    queryParams.push(`search=${encodeURIComponent(search)}`);
  }

  if (perPage) {
    queryParams.push(`per-page=${perPage}`);
  }

  if (sort) {
    queryParams.push(`sort=${sort}`);
  }

  if (restrictions) {
    queryParams.push(`restrictions=${restrictions}`);
  }

  return queryParams.join("&");
};

/**
 * Checks if WebRTC supported in browser
 */
export const DetectRTC: any = {
  isSupported(): boolean {
    return [
      "RTCPeerConnection",
      "webkitRTCPeerConnection",
      "mozRTCPeerConnection",
      "RTCIceGatherer",
    ].some((item: string) => {
      return item in window;
    });
  },
};

/**
 * Values like "Hello % You" (which contains regular % symbol) will throw an error on decodeURIComponent.
 * In this case, we just need to return passed string.
 */
export const decodeSipHeader = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};
